import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  DEFAULT_CURRENCY_CODE,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from "@angular/router";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { routes } from "./app.routes";
import { providePrimeNG } from "primeng/config";
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, HTTP_INTERCEPTORS } from "@angular/common/http";
import { environment } from "@environments/environment";
import { initializeTranslation, provideTranslation } from "@app/kernel/translations/translation.config";
import { TranslationService } from "@kernel/translations/translation.service";
import { provideMessage } from "@kernel/tools/message/message.config";
import { provideGraphQL } from "@kernel/graphql/graphql.config";
import { provideConfirmationDialog } from "@kernel/popups/popup/modules/confirmation-dialog/confirmation-dialog.config";
import { JwtModule } from "@auth0/angular-jwt";
import { provideFirebase } from "@kernel/notifications/filrebase.config";
import { pendingRequestsInterceptor$ } from "ng-http-loader";
import { JwtInterceptor } from "@app/shared/general/interceptor/jwt.interceptor";
import { AuthService } from "@kernel/tools/services/auth.service";
import { AuthServiceImpl } from "./shared/authentication/services/auth.servcice";
import { loadLocale } from "./shared/general/locale";
import { LocalStorageService } from "@kernel/tools/services/local-storage.service";
import { ErrorInterceptor } from "@app/shared/general/interceptor/error.interceptor";
import { laraTheme } from "@app/shared/general/config/primeng";
import { provideNgxStripe } from "ngx-stripe";
import { SubscriptionService } from "@app/shared/payment/services/subscription.service";
import { CreditService } from "@app/shared/payment/services/credit.service";
import { PaymentService } from "@app/shared/payment/services/payment.service";
import { CurrencyPipe } from "@angular/common";
import { AuthApiService } from "@app/shared/authentication/services/auth-api.service";

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => initializeTranslation(inject(TranslationService))),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: "reload",
        paramsInheritanceStrategy: "always",
      }),
      withInMemoryScrolling({
        anchorScrolling: "enabled",
        scrollPositionRestoration: "enabled",
      })
    ),
    provideMessage(),
    providePrimeNG({
      theme: {
        preset: laraTheme,
        options: { darkModeSelector: ".my-app-dark" },
      },
    }),
    provideGraphQL(environment.graphql_endpoint),
    provideTranslation(),
    provideConfirmationDialog(),
    provideFirebase(),
    provideNgxStripe(environment.STRIP_KEY),
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: () => inject(LocalStorageService).get<string>("token"),
        },
      })
    ),
    AuthApiService,
    // HTTP Interceptors
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([pendingRequestsInterceptor$])),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    // Auth Service mapping
    { provide: AuthService, useExisting: AuthServiceImpl },

    // Locale & Currency
    { provide: LOCALE_ID, useFactory: loadLocale, deps: [LocalStorageService] },
    { provide: DEFAULT_CURRENCY_CODE, useValue: "EUR" },
    PaymentService,
    CurrencyPipe,
    SubscriptionService,
    CreditService,
  ],
};
